import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import SearchField from 'components/fields/SearchField';

const ALL_VALUE = 'all';

const resolveLabel = (selected, options, allLabel) => {
  if (!selected.length) {
    return allLabel;
  }
  if (selected.length === 1) {
    return (
      options.find((option) => option.value === selected[0])?.label ??
      selected[0]
    );
  }
  return `${selected.length} selected`;
};

const sameSelection = (a, b) =>
  a.length === b.length && a.every((value) => b.includes(value));

/**
 * Multi-select dropdown with checkboxes and an "All" entry that clears the
 * selection, so an empty selection means "all". An optional search filters
 * the options by label.
 *
 * Checkbox clicks only update a local draft; the selection is committed
 * once, when the menu closes.
 */
const CheckboxMultiSelect = ({
  labelId,
  allLabel,
  ariaLabel,
  options,
  selected,
  onChange,
  searchPlaceholder
}) => {
  const [draft, setDraft] = useState(selected);
  const [search, setSearch] = useState('');
  const query = search.trim().toLowerCase();
  const visibleOptions = query
    ? options.filter((option) => option.label.toLowerCase().includes(query))
    : options;

  // Adopt external changes to the committed selection (e.g. location switch reset).
  useEffect(() => {
    setDraft(selected);
  }, [selected]);

  const isAll = !draft.length;

  const handleChange = (rawValues) => {
    // The search header is cloned as a value-less option; ignore it.
    const values = rawValues.filter((value) => value !== undefined);
    // "All" was clicked, or every option got selected — collapse to "all".
    if (
      values[values.length - 1] === ALL_VALUE ||
      !values.length ||
      values.filter((value) => value !== ALL_VALUE).length === options.length
    ) {
      setDraft([]);
      return;
    }
    setDraft(values.filter((value) => value !== ALL_VALUE));
  };

  const handleClose = () => {
    setSearch('');
    if (!sameSelection(draft, selected)) {
      onChange(draft);
    }
  };

  return (
    <div className='Say2EatDrop DropPeriod'>
      <FormControl fullWidth variant='outlined'>
        <InputLabel id={labelId} className='customRangeLabel'>
          {resolveLabel(draft, options, allLabel)}
        </InputLabel>
        <Select
          multiple
          labelId={labelId}
          value={isAll ? [ALL_VALUE] : draft}
          onChange={(event) => handleChange(event.target.value)}
          onClose={handleClose}
          inputProps={{'aria-label': ariaLabel}}
          renderValue={() => ''}
          MenuProps={{
            anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
            transformOrigin: {vertical: 'top', horizontal: 'left'},
            getContentAnchorEl: null,
            // The search field takes focus instead of the first option.
            autoFocus: !searchPlaceholder,
            onEntered: searchPlaceholder
              ? (node) => node.querySelector('input')?.focus()
              : undefined
          }}
        >
          {searchPlaceholder && (
            <div
              key='search'
              role='presentation'
              style={{
                position: 'sticky',
                top: 0,
                background: 'white',
                zIndex: 100,
                padding: '8px 10px'
              }}
            >
              {/* Stops clicks before MUI's option handler on the outer div */}
              <div role='presentation' onClick={(e) => e.stopPropagation()}>
                <SearchField
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onClear={() => setSearch('')}
                  isClearIconVisible={search.length > 0}
                  onKeyDown={(e) => e.key !== 'Escape' && e.stopPropagation()}
                />
              </div>
            </div>
          )}
          <MenuItem value={ALL_VALUE} className='reportTypeItem' disableRipple>
            <FormControlLabel
              className='Say2EatCheckbox'
              control={<Checkbox style={{color: '#FF2D38'}} checked={isAll} />}
            />
            {allLabel}
          </MenuItem>
          {visibleOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              className='reportTypeItem'
              disableRipple
            >
              <FormControlLabel
                className='Say2EatCheckbox'
                control={
                  <Checkbox
                    style={{color: '#FF2D38'}}
                    checked={draft.includes(option.value)}
                  />
                }
              />
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};

CheckboxMultiSelect.propTypes = {
  labelId: PropTypes.string.isRequired,
  allLabel: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  searchPlaceholder: PropTypes.string
};

CheckboxMultiSelect.defaultProps = {
  searchPlaceholder: null
};

export default CheckboxMultiSelect;
